import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import {
  PartnerSchedulingImportRun,
  PartnerSchedulingImportRunStatus,
} from '../entities/PartnerSchedulingImportRun.js';
import { parsePartnerWorkbook } from './partner-import-parse.js';
import { PartnerSchedulingClientRecord } from '../entities/PartnerSchedulingClientRecord.js';
import { PartnerSchedulingPartner } from '../entities/PartnerSchedulingPartner.js';
import type { PartnerSchedulingClientRecordInput, PartnerSchedulingPartnerInput } from './partner-import-types.js';
import { syncPartnerSchedulingClientStatus } from './sync-client-status.js';
import { PARTNER_IMPORT_STALE_RUNNING_MINUTES_DEFAULT } from '../ticketing/partner-scheduling-settings.js';
import { A } from '@adornis/base/env-info.js';
import { updateImportStatus } from '../api/subscribe-import-status.js';

/** BSON document max is 16MB; skipped-row diagnostics can be one string per row. */
const MAX_PERSISTED_MESSAGE_LINES = 1000;

const STALE_RUNNING_IMPORT_FAIL_MESSAGE =
  'Import run abandoned: RUNNING exceeded stale timeout (process crash, restart, or import longer than PARTNER_IMPORT_STALE_RUNNING_MINUTES).';

function capMessagesForPersistence(messages: string[]): string[] {
  if (messages.length <= MAX_PERSISTED_MESSAGE_LINES) return messages;

  const max = MAX_PERSISTED_MESSAGE_LINES;
  const head = messages[0] !== undefined ? [messages[0]] : [];
  const rest = messages.slice(1);
  const restSlots = Math.max(0, max - head.length - 1);
  const keptRest = rest.slice(0, restSlots);
  const omitted = rest.length - keptRest.length;
  const summary = `... ${omitted} more line${omitted === 1 ? '' : 's'} omitted (${messages.length} lines total)`;
  return [...head, ...keptRest, summary];
}

/**
 * Marks stale RUNNING import documents as FAILED so they no longer block new imports.
 * @returns how many documents were updated
 */
export async function expireStalePartnerImportRuns(): Promise<number> {
  const staleMinutes = PARTNER_IMPORT_STALE_RUNNING_MINUTES_DEFAULT;
  const staleBefore = new Date(Date.now() - staleMinutes * 60 * 1000);
  const coll = await getRawCollection<PartnerSchedulingImportRun>(PartnerSchedulingImportRun._collectionName);
  const result = await coll.updateMany(
    {
      status: PartnerSchedulingImportRunStatus.RUNNING,
      $or: [{ importedAt: { $lt: staleBefore } }, { importedAt: null }],
    },
    {
      $set: {
        status: PartnerSchedulingImportRunStatus.FAILED,
        messages: capMessagesForPersistence([STALE_RUNNING_IMPORT_FAIL_MESSAGE]),
      },
    },
  );
  const modified = result.modifiedCount ?? 0;
  if (modified > 0) {
    logger.warn({ modified, staleMinutes }, 'PARTNER::Marked stale RUNNING partner import runs as FAILED');
  }
  return modified;
}

export async function reservePartnerImportRun(sourceFilename: string): Promise<PartnerSchedulingImportRun> {
  const run = new PartnerSchedulingImportRun({
    _id: A.getGloballyUniqueID(),
    sourceFilename,
    importedAt: new Date(),
    status: PartnerSchedulingImportRunStatus.RUNNING,
    clientsImported: 0,
    partnersImported: 0,
    messages: [],
  });
  try {
    await run.create();
    updateImportStatus({
      status: PartnerSchedulingImportRunStatus.RUNNING,
      messages: [`Import run reserved: ${sourceFilename}. Running...`],
    });
    return run;
  } catch (error) {
    logger.error({ sourceFilename, error }, 'PARTNER::Failed to create import run document');
    throw new Error('Failed to reserve partner import run. Please try again.');
  }
}

/**
 * Parses the workbook, replaces partner DB collections, and updates the import run row.
 * Expires stale RUNNING rows, then blocks if another import is already in progress.
 */
export async function partnerWorkbookImport(
  base64Content: string,
  sourceFilename: string,
): Promise<PartnerSchedulingImportRun> {
  await expireStalePartnerImportRuns();
  const importRunCollection = await getRawCollection<PartnerSchedulingImportRun>(
    PartnerSchedulingImportRun._collectionName,
  );
  const activeRunning = await importRunCollection.findOne(
    { status: PartnerSchedulingImportRunStatus.RUNNING },
    { sort: { importedAt: -1 } },
  );
  if (activeRunning) {
    throw new Error('An import run is already in progress. Please wait for it to finish.');
  }

  const importRun = await reservePartnerImportRun(sourceFilename);
  logger.info({ importRunId: importRun._id, sourceFilename }, 'PARTNER::Import run reserved');
  try {
    const parsed = parsePartnerWorkbook(Buffer.from(base64Content, 'base64'), sourceFilename);
    const messagesForStorage = capMessagesForPersistence(parsed.messages);
    const clientRecordCollection = await getRawCollection<PartnerSchedulingClientRecordInput>(
      PartnerSchedulingClientRecord._collectionName,
    );
    const partnerRecordCollection = await getRawCollection<PartnerSchedulingPartnerInput>(
      PartnerSchedulingPartner._collectionName,
    );

    await Promise.all([clientRecordCollection.deleteMany({}), partnerRecordCollection.deleteMany({})]);

    const duplicatePartnerNames = [
      ...new Set(
        parsed.partners
          .filter(partner => parsed.partners.filter(p => p.partnerName === partner.partnerName).length > 1)
          .map(p => p.partnerName),
      ),
    ];
    if (duplicatePartnerNames.length > 0) {
      throw new Error(`Duplicate partner names found in the workbook: ${duplicatePartnerNames.join(', ')}`);
    }

    if (parsed.clients.length > 0) {
      await clientRecordCollection.insertMany(
        parsed.clients.map(client => ({
          ...client,
          _class: PartnerSchedulingClientRecord._class,
        })),
      );
    }
    if (parsed.partners.length > 0) {
      await partnerRecordCollection.insertMany(
        parsed.partners.map(partner => ({
          ...partner,
          _class: PartnerSchedulingPartner._class,
        })),
      );
    }
    await syncPartnerSchedulingClientStatus(parsed.clients);

    await importRunCollection.updateOne(
      { _id: importRun._id },
      {
        $set: {
          importedAt: new Date(),
          clientsImported: parsed.clients.length,
          partnersImported: parsed.partners.length,
          messages: messagesForStorage,
          status: PartnerSchedulingImportRunStatus.SUCCESS,
        },
      },
    );
    logger.info({ importRunId: importRun._id, sourceFilename }, 'PARTNER::Import run completed');
    updateImportStatus({
      status: PartnerSchedulingImportRunStatus.SUCCESS,
      messages: messagesForStorage,
    });
    return importRun;
  } catch (error) {
    logger.error({ importRunId: importRun._id, sourceFilename, error }, 'PARTNER::Import run failed');
    await importRunCollection.updateOne(
      { _id: importRun._id },
      {
        $set: {
          status: PartnerSchedulingImportRunStatus.FAILED,
          messages: capMessagesForPersistence([error instanceof Error ? error.message : 'Unknown error']),
        },
      },
    );
    updateImportStatus({
      status: PartnerSchedulingImportRunStatus.FAILED,
      messages: capMessagesForPersistence([error instanceof Error ? error.message : 'Unknown error']),
    });
    return importRun;
  }
}
